import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  SheetNoOverlay,
  SheetNoOverlayContent,
  SheetNoOverlayTitle,
  SheetNoOverlayHeader,
} from '../../SheetNoOverlay';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { X, Keyboard, ArrowLeft, LayoutGrid, MapPin, Edit } from 'lucide-react';
import { useMediaQuery } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useLightingEffects } from '@/contexts/LightingEffectsContext';
import type { ControlType, ControlAction } from '@/types/lighting';
import { updateLightingControl } from '@/services/lightingControlService';
import { logger } from '@/lib/logger';

export function PresentationDrawerV2() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [presentationCircuitId, setPresentationCircuitId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'loads' | 'controls' | 'lv-equipment'>('loads');
  const [testModeKeypad, setTestModeKeypad] = useState<ControlType | null>(null);
  const [editingButtonIndex, setEditingButtonIndex] = useState<number | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [lvViewMode, setLvViewMode] = useState<'by-room' | 'by-category'>('by-category');
  const [controlsViewMode, setControlsViewMode] = useState<'by-room' | 'by-type'>('by-type');
  const [activeButtonIndices, setActiveButtonIndices] = useState<Set<number>>(new Set());

  // Get state and actions from app store
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const setPresentationMode = useAppStore(state => state.setPresentationMode);
  const clearAllSelections = useAppStore(state => state.clearAllSelections);
  const selectEntity = useAppStore(state => state.selectEntity);
  const updateViewport = useAppStore(state => state.updateViewport);
  const presentationControlId = useAppStore(state => state.ui.presentationControlId);
  const presentationLVProductIds = useAppStore(state => state.ui.presentationLVProductIds);
  const setPresentationControlId = useAppStore(state => state.setPresentationControlId);
  const setPresentationLVProductIds = useAppStore(state => state.setPresentationLVProductIds);

  // Lighting effects for simulation
  const { startSim, stopSim } = useLightingEffects();

  const open = activeDrawer === 'presentation';

  // Get floor plan context and data
  const { floorPlan, data } = useFloorPlanContext();
  const {
    circuits = [],
    controls = [],
    fixtures = [],
    rooms = [],
    lvProducts = [],
    isLoading: dataLoading,
  } = data;

  // Sort circuits by sequence number
  const sortedCircuits = [...circuits].sort((a, b) => {
    const aSeq = a.sequence_number || 0;
    const bSeq = b.sequence_number || 0;
    return aSeq - bSeq;
  });

  // Clear presentation selections when drawer closes
  useEffect(() => {
    if (!open) {
      setPresentationCircuitId(null);
      setPresentationControlId(null);
      setPresentationLVProductIds([]);
    }
  }, [open, setPresentationCircuitId, setPresentationControlId, setPresentationLVProductIds]);

  // Helper function to format category names (snake_case to Title Case)
  const formatCategoryName = (category: string | null | undefined): string => {
    if (!category) return 'Uncategorized';
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to get the grouping key (subcategory if exists, otherwise category)
  const getProductGroupingKey = (product: (typeof lvProducts)[0]): string => {
    if (product.subcategory) return product.subcategory;
    return product.category || 'Uncategorized';
  };

  // Helper function to get display name (Category > Subcategory or just Category)
  const getProductDisplayName = (product: (typeof lvProducts)[0]): string => {
    const category = formatCategoryName(product.category);
    const subcategory = formatCategoryName(product.subcategory);

    if (product.subcategory) {
      return `${category} > ${subcategory}`;
    }
    return category;
  };

  // Helper function to get room names for a circuit
  const getCircuitRooms = useCallback(
    (circuitId: string): string => {
      const circuitFixtures = fixtures.filter(f => f.circuit_id === circuitId);
      const uniqueRoomIds = [...new Set(circuitFixtures.map(f => f.room_id).filter(Boolean))];
      const roomNames = uniqueRoomIds
        .map(roomId => rooms.find(r => r.id === roomId)?.name)
        .filter(Boolean) as string[];

      return roomNames.length > 0 ? roomNames.join(', ') : 'No room';
    },
    [fixtures, rooms],
  );

  // Helper function to check if control has configured actions with loads
  const hasConfiguredActions = useCallback((control: ControlType): boolean => {
    if (!control.actions || !Array.isArray(control.actions)) return false;

    // Check if any action has circuitIds
    // Actions might be ControlAction objects OR arrays of circuit IDs
    return control.actions.some(action => {
      // If action is a ControlAction object
      if (action && typeof action === 'object' && 'circuitIds' in action) {
        return (
          action.circuitIds && Array.isArray(action.circuitIds) && action.circuitIds.length > 0
        );
      }
      // If action is directly an array of circuit IDs
      if (Array.isArray(action)) {
        return action.length > 0;
      }
      return false;
    });
  }, []);

  // Visual order arrays for arrow key navigation
  const visuallyOrderedControls = useMemo(() => {
    if (controlsViewMode === 'by-type') {
      // By-type: order by type groups, then by controls within each group
      const typeOrder = ['dimmer', 'switch', 'keypad', 'auxiliary', 'centralized-keypad'];
      const ordered: ControlType[] = [];
      typeOrder.forEach(type => {
        const typeControls = controls.filter(c => c.type === type);
        ordered.push(...typeControls);
      });
      return ordered;
    } else {
      // By-room: order by rooms (with rooms first, then no-room), then by type within each room
      const controlsWithRooms = controls.filter(c => c.room_id);
      const controlsWithoutRooms = controls.filter(c => !c.room_id);

      // Group by room
      const roomMap = new Map<string, ControlType[]>();
      controlsWithRooms.forEach(control => {
        const roomId = control.room_id!;
        if (!roomMap.has(roomId)) roomMap.set(roomId, []);
        roomMap.get(roomId)!.push(control);
      });

      // Sort rooms alphabetically
      const sortedRoomIds = Array.from(roomMap.keys()).sort((a, b) => {
        const roomA = rooms.find(r => r.id === a)?.name || '';
        const roomB = rooms.find(r => r.id === b)?.name || '';
        return roomA.localeCompare(roomB);
      });

      const ordered: ControlType[] = [];
      // Add controls from each room (grouped by type within room)
      sortedRoomIds.forEach(roomId => {
        const roomControls = roomMap.get(roomId)!;
        // Group by type within room
        const typeMap = new Map<string, ControlType[]>();
        roomControls.forEach(c => {
          if (!typeMap.has(c.type)) typeMap.set(c.type, []);
          typeMap.get(c.type)!.push(c);
        });
        const sortedTypes = Array.from(typeMap.keys()).sort();
        sortedTypes.forEach(type => {
          ordered.push(...typeMap.get(type)!);
        });
      });

      // Add no-room controls at the end (grouped by type)
      if (controlsWithoutRooms.length > 0) {
        const typeMap = new Map<string, ControlType[]>();
        controlsWithoutRooms.forEach(c => {
          if (!typeMap.has(c.type)) typeMap.set(c.type, []);
          typeMap.get(c.type)!.push(c);
        });
        const sortedTypes = Array.from(typeMap.keys()).sort();
        sortedTypes.forEach(type => {
          ordered.push(...typeMap.get(type)!);
        });
      }

      return ordered;
    }
  }, [controls, controlsViewMode, rooms]);

  const visuallyOrderedLVProducts = useMemo(() => {
    if (lvViewMode === 'by-category') {
      // By-category: match exact render order (sorted by room within each category)
      const productsWithRooms = lvProducts.filter(p => p.room_id);
      const productsWithoutRooms = lvProducts.filter(p => !p.room_id);

      const categoryMap = new Map<string, typeof lvProducts>();
      // Add products with rooms first, then products without rooms
      [...productsWithRooms, ...productsWithoutRooms].forEach(product => {
        const category = product.category || 'Uncategorized';
        if (!categoryMap.has(category)) categoryMap.set(category, []);
        categoryMap.get(category)!.push(product);
      });

      const sortedCategories = Array.from(categoryMap.keys()).sort();
      const ordered: typeof lvProducts = [];
      sortedCategories.forEach(category => {
        const categoryProducts = categoryMap.get(category)!;

        // Sort products within category by room first, then by subcategory/name
        const sortedProducts = [...categoryProducts].sort((a, b) => {
          const roomA = a.room_id ? rooms.find(r => r.id === a.room_id) : null;
          const roomB = b.room_id ? rooms.find(r => r.id === b.room_id) : null;
          const roomNameA = roomA?.name || 'zzz_no_room'; // Sort no-room items last
          const roomNameB = roomB?.name || 'zzz_no_room';

          // First sort by room name
          const roomCompare = roomNameA.localeCompare(roomNameB);
          if (roomCompare !== 0) return roomCompare;

          // If same room, sort by subcategory then name
          const subcatA = a.subcategory || '';
          const subcatB = b.subcategory || '';
          const subcatCompare = subcatA.localeCompare(subcatB);
          if (subcatCompare !== 0) return subcatCompare;

          // Finally by product name
          return a.name.localeCompare(b.name);
        });

        ordered.push(...sortedProducts);
      });
      return ordered;
    } else {
      // By-room: hierarchical structure (room > category > subcategory)
      const productsWithRooms = lvProducts.filter(p => p.room_id);
      const productsWithoutRooms = lvProducts.filter(p => !p.room_id);

      // Group by room, then by category, then by subcategory
      const roomMap = new Map<string, Map<string, Map<string | null, typeof lvProducts>>>();
      productsWithRooms.forEach(product => {
        const roomId = product.room_id!;
        const category = product.category || 'Uncategorized';
        const subcategory = product.subcategory || null;

        if (!roomMap.has(roomId)) roomMap.set(roomId, new Map());
        const categoryMap = roomMap.get(roomId)!;
        if (!categoryMap.has(category)) categoryMap.set(category, new Map());
        const subcategoryMap = categoryMap.get(category)!;
        if (!subcategoryMap.has(subcategory)) subcategoryMap.set(subcategory, []);
        subcategoryMap.get(subcategory)!.push(product);
      });

      // Sort rooms alphabetically
      const sortedRoomIds = Array.from(roomMap.keys()).sort((a, b) => {
        const roomA = rooms.find(r => r.id === a)?.name || '';
        const roomB = rooms.find(r => r.id === b)?.name || '';
        return roomA.localeCompare(roomB);
      });

      const ordered: typeof lvProducts = [];

      // Add one representative product per subcategory per category per room
      sortedRoomIds.forEach(roomId => {
        const categoryMap = roomMap.get(roomId)!;
        const sortedCategories = Array.from(categoryMap.keys()).sort();

        sortedCategories.forEach(category => {
          const subcategoryMap = categoryMap.get(category)!;
          const sortedSubcategories = Array.from(subcategoryMap.keys()).sort((a, b) => {
            if (a === null) return 1;
            if (b === null) return -1;
            return a.localeCompare(b);
          });

          sortedSubcategories.forEach(subcategory => {
            const subcategoryProducts = subcategoryMap.get(subcategory)!;
            ordered.push(subcategoryProducts[0]);
          });
        });
      });

      // Add no-room products at the end (one per subcategory per category)
      if (productsWithoutRooms.length > 0) {
        const categoryMap = new Map<string, Map<string | null, typeof lvProducts>>();
        productsWithoutRooms.forEach(product => {
          const category = product.category || 'Uncategorized';
          const subcategory = product.subcategory || null;

          if (!categoryMap.has(category)) categoryMap.set(category, new Map());
          const subcategoryMap = categoryMap.get(category)!;
          if (!subcategoryMap.has(subcategory)) subcategoryMap.set(subcategory, []);
          subcategoryMap.get(subcategory)!.push(product);
        });

        const sortedCategories = Array.from(categoryMap.keys()).sort();
        sortedCategories.forEach(category => {
          const subcategoryMap = categoryMap.get(category)!;
          const sortedSubcategories = Array.from(subcategoryMap.keys()).sort((a, b) => {
            if (a === null) return 1;
            if (b === null) return -1;
            return a.localeCompare(b);
          });

          sortedSubcategories.forEach(subcategory => {
            const subcategoryProducts = subcategoryMap.get(subcategory)!;
            ordered.push(subcategoryProducts[0]);
          });
        });
      }

      return ordered;
    }
  }, [lvProducts, lvViewMode, rooms]);

  // Choose Drawer for mobile, SheetNoOverlay for desktop
  const Panel = isMobile ? Drawer : SheetNoOverlay;
  const PanelContent = isMobile ? DrawerContent : SheetNoOverlayContent;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        // Check if we're switching to control drawer from test mode
        const isEditingFromTestMode = sessionStorage.getItem('testModeReturn');

        setActiveDrawer(null);
        setPresentationMode(false);
        setPresentationCircuitId(null);
        setPresentationControlId(null);
        setPresentationLVProductIds([]);
        clearAllSelections();

        // Only clear test mode state if NOT going to control drawer for editing
        if (!isEditingFromTestMode) {
          setTestModeKeypad(null);
          setEditingButtonIndex(null);
          setActiveButtonIndices(new Set());
        }

        stopSim();
      } else {
        // Default to controls tab when opening
        setActiveTab('controls');
      }
    },
    [
      setActiveDrawer,
      setPresentationMode,
      clearAllSelections,
      setPresentationLVProductIds,
      stopSim,
    ],
  );

  const updateKeypadLabel = useCallback(
    async (index: number, newLabel: string) => {
      if (!testModeKeypad) return;

      try {
        const labels = [...(testModeKeypad.labels || [])];
        labels[index] = newLabel.trim();

        await updateLightingControl(testModeKeypad.id, { labels });
        await data.refetch();

        // Update local state
        setTestModeKeypad(prev => (prev ? { ...prev, labels } : null));
        toast.success('Label updated');
      } catch (error) {
        toast.error('Failed to update label');
      }
    },
    [testModeKeypad, data],
  );

  // Smooth animation function with cancellation
  const animationFrameIdRef = useRef<number | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const originalTargetRef = useRef<{ scale: number; positionX: number; positionY: number } | null>(
    null,
  );

  const animateViewport = useCallback(
    (target: { scale: number; positionX: number; positionY: number }, duration: number) => {
      // 🐛 FIX: Declare hadPreviousAnimation at function scope so it's accessible in all try-catch blocks
      let hadPreviousAnimation = false;

      try {
        // CRITICAL FIX: Cancel any existing animation before starting a new one
        hadPreviousAnimation = animationFrameIdRef.current !== null;
        if (animationFrameIdRef.current !== null) {
          cancelAnimationFrame(animationFrameIdRef.current);
          animationFrameIdRef.current = null;
          logger.log('[animateViewport] ⚠️ CANCELLED previous animation');
        }

        // CRITICAL: Clear any pending debounce timeout
        if (debounceTimeoutRef.current !== null) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
          logger.log('[animateViewport] 🧹 CLEARED pending debounce timeout');
        }

        // CRITICAL: Set isAnimating flag to block manual pan/zoom during animation
        logger.log('[animateViewport] 🚀 ENTRY POINT - About to set isAnimating flag');
        updateViewport({ isAnimating: true });
        logger.log('[animateViewport] ✓ isAnimating flag set successfully');

        // 🔒 CRITICAL: Store ORIGINAL target in ref to survive closure corruption
        originalTargetRef.current = { ...target };
        logger.log('[animateViewport] 🔒 Stored ORIGINAL target in ref:', {
          scale: target.scale.toFixed(4),
          positionX: target.positionX.toFixed(2),
          positionY: target.positionY.toFixed(2),
        });
      } catch (error) {
        logger.error('[animateViewport] ❌ CRITICAL ERROR in function entry:', error);
        // Reset animation flag on error
        updateViewport({ isAnimating: false });
        throw error;
      }

      try {
        const startTime = performance.now();
        logger.log('[animateViewport] 📊 Getting current viewport state...');
        const currentViewportBeforeCapture = useAppStore.getState().floorPlan.viewport;
        logger.log('[animateViewport] 📊 Current viewport retrieved successfully:', {
          scale: currentViewportBeforeCapture.scale,
          positionX: currentViewportBeforeCapture.positionX,
          positionY: currentViewportBeforeCapture.positionY,
          types: {
            scale: typeof currentViewportBeforeCapture.scale,
            positionX: typeof currentViewportBeforeCapture.positionX,
            positionY: typeof currentViewportBeforeCapture.positionY,
          },
        });
        const startViewport = { ...currentViewportBeforeCapture };
        logger.log('[animateViewport] 📊 startViewport spread completed');

        logger.log('[animateViewport] 🎬 NEW ANIMATION STARTING (BLOCKING MANUAL PAN/ZOOM):', {
          hadPreviousAnimation,
          isAnimating: true,
          startViewport: {
            scale: startViewport.scale,
            positionX: startViewport.positionX.toFixed(2),
            positionY: startViewport.positionY.toFixed(2),
          },
          target: {
            scale: target.scale,
            positionX: target.positionX.toFixed(2),
            positionY: target.positionY.toFixed(2),
          },
          delta: {
            scale: (target.scale - startViewport.scale).toFixed(4),
            positionX: (target.positionX - startViewport.positionX).toFixed(2),
            positionY: (target.positionY - startViewport.positionY).toFixed(2),
          },
          duration,
        });

        const animate = (currentTime: number) => {
          // 🔧 CRITICAL FIX: Check if animation was cancelled before processing this frame
          // This prevents the race condition where a frame executes after cancellation
          if (animationFrameIdRef.current === null && originalTargetRef.current === null) {
            logger.log('[animateViewport] Animation was cancelled, skipping this frame');
            return;
          }

          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Cubic ease-in-out
          const easeProgress =
            progress < 0.5
              ? 4 * progress * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 3) / 2;

          const scale = startViewport.scale + (target.scale - startViewport.scale) * easeProgress;
          const positionX =
            startViewport.positionX + (target.positionX - startViewport.positionX) * easeProgress;
          const positionY =
            startViewport.positionY + (target.positionY - startViewport.positionY) * easeProgress;

          // 🔧 CRITICAL FIX: MUST include isAnimating: true on EVERY frame!
          // Without this, the flag gets set to undefined, clearing the block on gesture handlers
          updateViewport({ scale, positionX, positionY, isAnimating: true });

          if (progress < 1) {
            animationFrameIdRef.current = requestAnimationFrame(animate);
          } else {
            animationFrameIdRef.current = null;
            // CRITICAL: Debounced re-enable to allow trackpad momentum to dissipate
            // Wait 500ms before setting isAnimating: false to prevent momentum corruption
            logger.log('[animateViewport] ⏱️ Animation COMPLETED - DEBOUNCING re-enable (500ms):', {
              final: { scale, positionX: positionX.toFixed(2), positionY: positionY.toFixed(2) },
              target: {
                scale: target.scale,
                positionX: target.positionX.toFixed(2),
                positionY: target.positionY.toFixed(2),
              },
              matches:
                Math.abs(scale - target.scale) < 0.01 &&
                Math.abs(positionX - target.positionX) < 0.5,
            });

            debounceTimeoutRef.current = setTimeout(() => {
              // 🔧 CRITICAL FIX: Check if debounce was cancelled before processing
              // This prevents the race condition where the timeout fires exactly when user clicks a Control
              // Timeline: Animation completes at 400ms, debounce scheduled for 900ms (400ms + 500ms)
              // If user clicks Control at ~900ms, handleSelectControl cancels refs, but this callback
              // might have already started executing. We detect that by checking if refs are null.
              if (debounceTimeoutRef.current === null || originalTargetRef.current === null) {
                logger.log('[animateViewport] Debounce was cancelled, skipping restoration');
                return;
              }

              // 🔧 FIX: Force-restore ORIGINAL target values before re-enabling gestures
              // Use ref instead of closure to get the TRUE original target
              const originalTarget = originalTargetRef.current;
              if (!originalTarget) {
                logger.error('[animateViewport] ❌ No original target found in ref!');
                return;
              }

              const beforeRestore = useAppStore.getState().floorPlan.viewport;
              const wasCorrupted =
                Math.abs(beforeRestore.scale - originalTarget.scale) > 0.01 ||
                Math.abs(beforeRestore.positionX - originalTarget.positionX) > 0.5 ||
                Math.abs(beforeRestore.positionY - originalTarget.positionY) > 0.5;

              if (wasCorrupted) {
                logger.warn(
                  '[animateViewport] 🔧 Viewport was corrupted - FORCE RESTORING ORIGINAL TARGET:',
                  {
                    corrupted: {
                      scale: beforeRestore.scale.toFixed(4),
                      positionX: beforeRestore.positionX.toFixed(2),
                      positionY: beforeRestore.positionY.toFixed(2),
                    },
                    originalTarget: {
                      scale: originalTarget.scale.toFixed(4),
                      positionX: originalTarget.positionX.toFixed(2),
                      positionY: originalTarget.positionY.toFixed(2),
                    },
                    difference: {
                      scale: (beforeRestore.scale - originalTarget.scale).toFixed(4),
                      positionX: (beforeRestore.positionX - originalTarget.positionX).toFixed(2),
                      positionY: (beforeRestore.positionY - originalTarget.positionY).toFixed(2),
                    },
                  },
                );
              }

              // Always force-restore ORIGINAL target values AND re-enable gestures in one atomic update
              updateViewport({
                scale: originalTarget.scale,
                positionX: originalTarget.positionX,
                positionY: originalTarget.positionY,
                isAnimating: false,
              });
              debounceTimeoutRef.current = null;

              logger.log('[animateViewport] ✅ Manual pan/zoom RE-ENABLED after debounce:', {
                wasCorrupted,
                finalViewport: {
                  scale: originalTarget.scale.toFixed(4),
                  positionX: originalTarget.positionX.toFixed(2),
                  positionY: originalTarget.positionY.toFixed(2),
                },
              });
            }, 500);
          }
        };

        // Check for reduced motion preference
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
          logger.log(
            '[animateViewport] Reduced motion - applying target immediately, debouncing re-enable',
          );
          // 🔧 CRITICAL FIX: Keep isAnimating: true during debounce period
          updateViewport({ ...target, isAnimating: true });
          animationFrameIdRef.current = null;

          // Also debounce re-enable for reduced motion path
          debounceTimeoutRef.current = setTimeout(() => {
            // 🔧 FIX: Force-restore ORIGINAL target values before re-enabling gestures (reduced motion path)
            const originalTarget = originalTargetRef.current;
            if (!originalTarget) {
              logger.error(
                '[animateViewport] ❌ No original target found in ref (reduced motion)!',
              );
              return;
            }

            updateViewport({
              scale: originalTarget.scale,
              positionX: originalTarget.positionX,
              positionY: originalTarget.positionY,
              isAnimating: false,
            });
            debounceTimeoutRef.current = null;
            logger.log('[animateViewport] ✅ Manual pan/zoom RE-ENABLED (reduced motion)');
          }, 500);
        } else {
          animationFrameIdRef.current = requestAnimationFrame(animate);
        }
      } catch (error) {
        logger.error('[animateViewport] ❌ CRITICAL ERROR during animation setup:', {
          error,
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          errorType: error?.constructor?.name,
        });
        // Reset animation flag on error
        updateViewport({ isAnimating: false });
        throw error;
      }
    },
    [updateViewport],
  );

  // Cleanup animation and debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      if (debounceTimeoutRef.current !== null) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // CRITICAL: Clear isAnimating flag on unmount
      updateViewport({ isAnimating: false });
    };
  }, [updateViewport]);

  // Smooth viewport animation to center on circuit
  const centerViewportOnCircuit = useCallback(
    (circuitId: string) => {
      // Find all fixtures that belong to this circuit
      const circuitFixtures = fixtures.filter(f => f.circuit_id === circuitId);

      if (circuitFixtures.length === 0) {
        toast.error('No fixtures found for this circuit');
        return;
      }

      // Calculate bounding box of all fixtures in the circuit
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      circuitFixtures.forEach(fixture => {
        const x = fixture.x_position || 0;
        const y = fixture.y_position || 0;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });

      // Add padding around the bounding box
      const padding = 100;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      // Calculate center point
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      // Get viewport container dimensions
      const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // 🎯 FIX: Account for drawer width when centering
      const drawerWidth = isMobile ? 320 : 380;
      const availableWidth = containerWidth - drawerWidth;

      // Calculate scale to fit the circuit
      const boxWidth = maxX - minX;
      const boxHeight = maxY - minY;

      // Avoid division by zero - use available width for scale calculation
      const scaleX = boxWidth > 0 ? availableWidth / boxWidth : 1;
      const scaleY = boxHeight > 0 ? containerHeight / boxHeight : 1;

      // Cap zoom between 0.1 and 2 for usability
      const scale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.1), 2);

      // Calculate position to center the circuit in the VISIBLE area (excluding drawer)
      const positionX = availableWidth / 2 - centerX * scale;
      const positionY = containerHeight / 2 - centerY * scale;

      // Animate viewport with easing
      animateViewport({ scale, positionX, positionY }, 400);
    },
    [fixtures, animateViewport, isMobile],
  );

  // Smooth viewport animation to center on control
  const centerViewportOnControl = useCallback(
    (controlId: string) => {
      // Find the control
      const control = controls.find(c => c.id === controlId);

      if (!control) {
        toast.error('Control not found');
        return;
      }

      if (
        control.x === null ||
        control.y === null ||
        control.x === undefined ||
        control.y === undefined
      ) {
        toast.error('Control has not been placed on the floor plan');
        return;
      }

      const centerX = control.x;
      const centerY = control.y;

      // Get viewport container dimensions
      const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
      if (!container) {
        logger.error('[centerViewportOnControl] ❌ Container .floor-plan-viewport not found!');
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // 🎯 FIX: Account for drawer width when centering
      const drawerWidth = isMobile ? 320 : 380;
      const availableWidth = containerWidth - drawerWidth;

      // Use 1.0x zoom for controls (same as LV products)
      const scale = 1.0;

      // Calculate position to center the control in the VISIBLE area (excluding drawer)
      const positionX = availableWidth / 2 - centerX * scale;
      const positionY = containerHeight / 2 - centerY * scale;

      // ✅ Instant jump - no animation to avoid coordinate corruption
      updateViewport({ scale, positionX, positionY });
    },
    [controls, updateViewport, isMobile],
  );

  // Keypad test mode handlers
  const enterTestMode = useCallback(
    (keypad: ControlType) => {
      setTestModeKeypad(keypad);
      setEditingButtonIndex(null);
      setLabelDraft('');
      // Select the control to make it blink/highlight on floor plan
      setPresentationControlId(keypad.id);
      // ✅ ENABLED: Center viewport on control when entering test mode
      centerViewportOnControl(keypad.id);
    },
    [setPresentationControlId, centerViewportOnControl],
  );

  const exitTestMode = useCallback(() => {
    setTestModeKeypad(null);
    setEditingButtonIndex(null);
    setLabelDraft('');
    setActiveButtonIndices(new Set());
    stopSim();
  }, [stopSim]);

  const handleSelectCircuit = useCallback(
    (circuitId: string | null) => {
      if (!circuitId) {
        setPresentationCircuitId(null);
        clearAllSelections();
        return;
      }

      setPresentationCircuitId(circuitId);
      // Clear other presentation states when selecting a circuit
      setPresentationControlId(null);
      setPresentationLVProductIds([]);
      // For circuits, we DO select them so loads blink
      selectEntity('circuit', circuitId);
      centerViewportOnCircuit(circuitId);
    },
    [
      clearAllSelections,
      centerViewportOnCircuit,
      setPresentationControlId,
      setPresentationLVProductIds,
      selectEntity,
    ],
  );

  const handleSelectControl = useCallback(
    (controlId: string | null) => {
      // 🎯 FIX: Let animateViewport handle cancellation internally (just like LV products do)
      // This matches the working behavior of handleSelectLVProduct
      // animateViewport has robust internal cancellation logic at lines 391-405
      logger.log('[handleSelectControl] 🖱️ CLICKED control:', {
        controlId,
        timestamp: performance.now(),
      });

      if (!controlId) {
        setPresentationControlId(null);
        clearAllSelections();
        return;
      }

      // Clear all selections to stop load blinking
      clearAllSelections();
      setPresentationControlId(controlId);
      // Clear other presentation states when selecting a control
      setPresentationCircuitId(null);
      setPresentationLVProductIds([]);

      // ✅ ENABLED: Center viewport on control using shared animation system
      centerViewportOnControl(controlId);
    },
    [
      clearAllSelections,
      setPresentationControlId,
      setPresentationCircuitId,
      setPresentationLVProductIds,
      centerViewportOnControl,
    ],
  );

  // Camera animation for LV products (similar to controls)
  const centerViewportOnLVProduct = useCallback(
    (productId: string) => {
      const product = lvProducts.find(p => p.id === productId);

      if (!product) {
        toast.error('Product not found');
        return;
      }

      const centerX = product.x_position;
      const centerY = product.y_position;

      // 🐛 CRITICAL DIAGNOSTIC FOR COORDINATE COMPARISON
      logger.log('[centerViewportOnLVProduct] 🔍 FULL PRODUCT DIAGNOSTIC:', {
        productId: product.id,
        productName: product.name,
        productCategory: product.category,
        coordinates: { x_position: product.x_position, y_position: product.y_position },
        centerCoordinates: { centerX, centerY },
        hasScale: product.scale !== undefined && product.scale !== null,
        scale: product.scale,
        hasRotation: product.rotation !== undefined && product.rotation !== null,
        rotation: product.rotation,
        roomId: product.room_id,
        fullProductObject: JSON.stringify(product, null, 2),
        timestamp: Date.now(),
      });

      // Get viewport container dimensions
      const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // 🎯 FIX: Account for drawer width when centering
      const drawerWidth = isMobile ? 320 : 380;
      const availableWidth = containerWidth - drawerWidth;

      // Use less zoom for AV products (zoom out more to show more context)
      const scale = 1.0;

      // Calculate position to center the product in the VISIBLE area (excluding drawer)
      const positionX = availableWidth / 2 - centerX * scale;
      const positionY = containerHeight / 2 - centerY * scale;

      // Animate viewport with easing
      animateViewport({ scale, positionX, positionY }, 400);
    },
    [lvProducts, animateViewport, isMobile],
  );

  // Camera animation for room (focus on all products in that category in the room)
  const centerViewportOnRoom = useCallback(
    (roomId: string, category: string) => {
      // Find all products of this category in this room
      const roomProducts = lvProducts.filter(
        p => p.room_id === roomId && matchesCategory(p.category, category),
      );

      if (roomProducts.length === 0) {
        toast.error('No products found in this room');
        return;
      }

      // Calculate bounding box of all products in the room
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      roomProducts.forEach(product => {
        const x = product.x_position;
        const y = product.y_position;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });

      // Add padding around the bounding box
      const padding = 100;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      // Calculate center point
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      // Get viewport container dimensions
      const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      // Calculate scale to fit the room
      const boxWidth = maxX - minX;
      const boxHeight = maxY - minY;

      const scaleX = boxWidth > 0 ? containerWidth / boxWidth : 1;
      const scaleY = boxHeight > 0 ? containerHeight / boxHeight : 1;

      // Cap zoom between 0.1 and 2
      const scale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.1), 2);

      // Calculate position to center the room in the viewport
      const positionX = containerWidth / 2 - centerX * scale;
      const positionY = containerHeight / 2 - centerY * scale;

      // Animate viewport with easing
      animateViewport({ scale, positionX, positionY }, 400);
    },
    [lvProducts, animateViewport, isMobile],
  );

  // Center viewport on all fixtures associated with circuit IDs (for test simulation)
  const centerViewportOnCircuits = useCallback(
    (circuitIds: string[]) => {
      if (!circuitIds || circuitIds.length === 0) return;

      // Get all fixtures for the given circuit IDs
      const circuitFixtures = fixtures.filter(
        f => f.circuit_id && circuitIds.includes(f.circuit_id),
      );

      if (circuitFixtures.length === 0) {
        logger.log('[PresentationDrawer] No fixtures found for circuits:', circuitIds);
        return;
      }

      // For single fixture, just center on it
      if (circuitFixtures.length === 1) {
        const fixture = circuitFixtures[0];
        const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();

        const scale = 1.5;
        const positionX = containerRect.width / 2 - fixture.x_position * scale;
        const positionY = containerRect.height / 2 - fixture.y_position * scale;

        animateViewport({ scale, positionX, positionY }, 400);
        return;
      }

      // Calculate bounding box of all fixtures
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      circuitFixtures.forEach(f => {
        minX = Math.min(minX, f.x_position);
        minY = Math.min(minY, f.y_position);
        maxX = Math.max(maxX, f.x_position);
        maxY = Math.max(maxY, f.y_position);
      });

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const boxWidth = maxX - minX + 300;
      const boxHeight = maxY - minY + 300;

      const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();

      const scaleX = boxWidth > 0 ? containerRect.width / boxWidth : 1;
      const scaleY = boxHeight > 0 ? containerRect.height / boxHeight : 1;
      const scale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.1), 2);

      const positionX = containerRect.width / 2 - centerX * scale;
      const positionY = containerRect.height / 2 - centerY * scale;

      logger.log('[PresentationDrawer] Centering viewport on circuits:', {
        circuitIds,
        fixtureCount: circuitFixtures.length,
        boundingBox: { minX, minY, maxX, maxY },
        scale,
      });

      animateViewport({ scale, positionX, positionY }, 400);
    },
    [fixtures, animateViewport, isMobile],
  );

  const handleSelectLVProduct = useCallback(
    (productId: string | null, groupKey: string) => {
      if (!productId) {
        setPresentationLVProductIds([]);
        clearAllSelections();
        return;
      }

      const product = lvProducts.find(p => p.id === productId);
      if (!product) return;

      // Clear all selections to stop load blinking
      clearAllSelections();
      // Clear other presentation states when selecting an LV product
      setPresentationCircuitId(null);
      setPresentationControlId(null);

      // Blinking behavior based on viewing mode
      if (lvViewMode === 'by-room' && product.room_id) {
        // In by-room mode: blink all products in the same grouping (subcategory or category) within that room
        const roomProducts = lvProducts.filter(p => {
          if (p.room_id !== product.room_id) return false;
          // Match by grouping key (subcategory if exists, otherwise category)
          const productGroupKey = getProductGroupingKey(p);
          return productGroupKey === groupKey;
        });
        const productIds = roomProducts.map(p => p.id);
        setPresentationLVProductIds(productIds);

        // Focus on all products in the room/category
        if (roomProducts.length > 1) {
          // Calculate bounding box of all products in the category
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          roomProducts.forEach(p => {
            minX = Math.min(minX, p.x_position);
            minY = Math.min(minY, p.y_position);
            maxX = Math.max(maxX, p.x_position);
            maxY = Math.max(maxY, p.y_position);
          });

          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          const boxWidth = maxX - minX + 100;
          const boxHeight = maxY - minY + 100;

          const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
          if (container) {
            const containerRect = container.getBoundingClientRect();

            const scaleX = boxWidth > 0 ? containerRect.width / boxWidth : 1;
            const scaleY = boxHeight > 0 ? containerRect.height / boxHeight : 1;
            const scale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.1), 2);

            const positionX = containerRect.width / 2 - centerX * scale;
            const positionY = containerRect.height / 2 - centerY * scale;

            animateViewport({ scale, positionX, positionY }, 400);
          }
        } else {
          centerViewportOnLVProduct(productId);
        }
      } else {
        // In by-category mode: only blink the selected element
        setPresentationLVProductIds([productId]);
        centerViewportOnLVProduct(productId);
      }
    },
    [
      clearAllSelections,
      centerViewportOnLVProduct,
      lvProducts,
      setPresentationLVProductIds,
      setPresentationControlId,
      lvViewMode,
      isMobile,
      animateViewport,
    ],
  );

  // Refresh test mode keypad when returning from edit drawer
  useEffect(() => {
    // When returning to presentation drawer from control drawer
    if (activeDrawer === 'presentation' && testModeKeypad) {
      // Find the updated control in the data
      const updatedControl = data.controls?.find(c => c.id === testModeKeypad.id);
      if (updatedControl) {
        logger.log('[PresentationDrawer] Refreshing test mode keypad with updated data');
        setTestModeKeypad(updatedControl as ControlType);
      }
    }
  }, [activeDrawer, data.controls, testModeKeypad?.id]);

  // Handle ESC key in test mode to clear active button toggles
  useEffect(() => {
    if (!open || !testModeKeypad) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeButtonIndices.size > 0) {
        e.preventDefault();
        setActiveButtonIndices(new Set());
        stopSim();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, testModeKeypad, activeButtonIndices, stopSim]);

  // Handle arrow key navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC key: Clear all presentation selections
      if (e.key === 'Escape') {
        e.preventDefault();
        setPresentationCircuitId(null);
        setPresentationControlId(null);
        setPresentationLVProductIds([]);
        return;
      }

      if (activeTab === 'loads') {
        if (sortedCircuits.length === 0) return;

        const currentIndex = sortedCircuits.findIndex(c => c.id === presentationCircuitId);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % sortedCircuits.length;
          handleSelectCircuit(sortedCircuits[nextIndex].id);
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevIndex =
            currentIndex === -1
              ? sortedCircuits.length - 1
              : (currentIndex - 1 + sortedCircuits.length) % sortedCircuits.length;
          handleSelectCircuit(sortedCircuits[prevIndex].id);
        }
      } else if (activeTab === 'controls') {
        if (visuallyOrderedControls.length === 0) return;

        const currentIndex = visuallyOrderedControls.findIndex(c => c.id === presentationControlId);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex =
            currentIndex === -1 ? 0 : (currentIndex + 1) % visuallyOrderedControls.length;
          handleSelectControl(visuallyOrderedControls[nextIndex].id);
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevIndex =
            currentIndex === -1
              ? visuallyOrderedControls.length - 1
              : (currentIndex - 1 + visuallyOrderedControls.length) %
                visuallyOrderedControls.length;
          handleSelectControl(visuallyOrderedControls[prevIndex].id);
        }
      } else if (activeTab === 'lv-equipment') {
        if (visuallyOrderedLVProducts.length === 0) return;

        const currentIndex = visuallyOrderedLVProducts.findIndex(
          p => p.id === presentationLVProductIds[0],
        );

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex =
            currentIndex === -1 ? 0 : (currentIndex + 1) % visuallyOrderedLVProducts.length;
          const nextProduct = visuallyOrderedLVProducts[nextIndex];
          const nextGroupKey = getProductGroupingKey(nextProduct);
          handleSelectLVProduct(nextProduct.id, nextGroupKey);
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevIndex =
            currentIndex === -1
              ? visuallyOrderedLVProducts.length - 1
              : (currentIndex - 1 + visuallyOrderedLVProducts.length) %
                visuallyOrderedLVProducts.length;
          const prevProduct = visuallyOrderedLVProducts[prevIndex];
          const prevGroupKey = getProductGroupingKey(prevProduct);
          handleSelectLVProduct(prevProduct.id, prevGroupKey);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    open,
    activeTab,
    sortedCircuits,
    visuallyOrderedControls,
    visuallyOrderedLVProducts,
    presentationCircuitId,
    presentationControlId,
    presentationLVProductIds,
    handleSelectCircuit,
    handleSelectControl,
    handleSelectLVProduct,
  ]);

  // Ensure selected circuit stays visible in list
  useEffect(() => {
    if (!open || !presentationCircuitId) return;
    const el = document.querySelector(
      `[data-circuit-id="${presentationCircuitId}"]`,
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [presentationCircuitId, open]);

  // Ensure selected control stays visible in list
  useEffect(() => {
    if (!open || !presentationControlId) return;
    const el = document.querySelector(
      `[data-control-id="${presentationControlId}"]`,
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [presentationControlId, open]);

  // Ensure selected LV product stays visible in list
  useEffect(() => {
    const focusedProductId = presentationLVProductIds[0];
    if (!open || !focusedProductId) return;
    const el = document.querySelector(
      `[data-lv-product-id="${focusedProductId}"]`,
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [presentationLVProductIds, open]);

  // Toggle button handler with simulation (mutually exclusive - only one button active at a time)
  const handleToggleButton = useCallback(
    (buttonIndex: number) => {
      if (!testModeKeypad) return;

      const action = testModeKeypad.actions[buttonIndex];

      // Handle both ControlAction objects and direct arrays of circuit IDs
      let circuitIds: string[] = [];
      if (Array.isArray(action)) {
        circuitIds = action;
      } else if (action && typeof action === 'object' && 'circuitIds' in action) {
        circuitIds = (action as ControlAction).circuitIds || [];
      }

      if (circuitIds.length === 0) return;

      // Check if this button is currently active
      const isCurrentlyActive = activeButtonIndices.has(buttonIndex);

      if (isCurrentlyActive) {
        // Deactivate - stop simulation
        setActiveButtonIndices(new Set());
        stopSim();
      } else {
        // Activate - stop any existing simulation first, then start new one
        // This ensures only one button is active at a time
        stopSim();
        setActiveButtonIndices(new Set([buttonIndex]));
        startSim(circuitIds, 'on');
        // Center viewport to show all fixtures in this simulation
        centerViewportOnCircuits(circuitIds);
      }
    },
    [testModeKeypad, stopSim, startSim, activeButtonIndices, centerViewportOnCircuits],
  );

  // Render keypad test mode view
  const renderKeypadTestMode = () => {
    if (!testModeKeypad) return null;

    const buttonCount = testModeKeypad.number_of_buttons || 0;
    const labels = testModeKeypad.labels || [];
    const actions = testModeKeypad.actions || [];

    return (
      <>
        {/* Header with back and edit buttons */}
        {isMobile ? (
          <DrawerHeader className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                onClick={exitTestMode}
                aria-label="Back to controls"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <DrawerTitle className="text-base">Test Mode</DrawerTitle>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={() => handleOpenChange(false)}
              aria-label="Close drawer"
            >
              <X className="h-4 w-4" />
            </Button>
          </DrawerHeader>
        ) : (
          <SheetNoOverlayHeader className="flex items-center justify-start px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={exitTestMode}
              aria-label="Back to controls"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <SheetNoOverlayTitle>Test Mode</SheetNoOverlayTitle>
          </SheetNoOverlayHeader>
        )}

        {/* Control info header with consistent styling */}
        <div className="px-4 pt-4">
          <div className="bg-muted/50 border-l-4 border-muted-foreground rounded-r-md px-4 py-3 mb-4 flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-semibold">{testModeKeypad.name}</p>
              <p className="text-xs text-muted-foreground capitalize">
                {testModeKeypad.type.replace('-', ' ')} • {buttonCount} button
                {buttonCount !== 1 ? 's' : ''}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 -mt-1"
              onClick={() => {
                // Store test mode state before switching
                const testState = {
                  keypadId: testModeKeypad.id,
                  returnToPresentation: true,
                  activeButtonIndices: Array.from(activeButtonIndices), // Store active button indices
                };
                sessionStorage.setItem('testModeReturn', JSON.stringify(testState));
                // Select the control so the drawer can access it
                selectEntity('control', testModeKeypad.id);
                setActiveDrawer('control');
              }}
              aria-label="Edit button settings"
              title="Edit button settings"
            >
              <Edit className="h-3.5 w-3.5" />
              <span className="text-xs">Edit</span>
            </Button>
          </div>
        </div>

        {/* Vertical stacked button layout (like a real keypad) */}
        <div className="px-4 pb-4 flex items-center justify-center">
          <div className="border-2 rounded-lg bg-card shadow-md p-4 w-48">
            <div className="space-y-2">
              {Array.from({ length: buttonCount }).map((_, index) => {
                const label = labels[index] || `Button ${index + 1}`;
                const action = actions[index];

                // Handle both ControlAction objects and direct arrays of circuit IDs
                let circuitIds: string[] = [];
                if (action && typeof action === 'object' && 'circuitIds' in action) {
                  // ControlAction object - extract circuitIds array
                  circuitIds = (action as ControlAction).circuitIds || [];
                } else if (Array.isArray(action)) {
                  // Legacy format: direct array of circuit IDs
                  circuitIds = action;
                }

                const hasCircuits = circuitIds.length > 0;
                const isActive = activeButtonIndices.has(index);

                return (
                  <button
                    key={index}
                    onClick={() => {
                      if (hasCircuits) {
                        handleToggleButton(index);
                      }
                    }}
                    disabled={!hasCircuits}
                    className={cn(
                      'w-full rounded-md border-2 transition-all h-12 px-3 flex items-center justify-center',
                      hasCircuits
                        ? isActive
                          ? 'bg-primary text-primary-foreground border-primary shadow-md cursor-pointer'
                          : 'bg-background hover:bg-accent/50 border-border hover:border-primary/50 cursor-pointer'
                        : 'bg-muted/50 border-dashed border-muted-foreground/30 cursor-not-allowed opacity-50 pointer-events-none',
                    )}
                    title={
                      hasCircuits
                        ? isActive
                          ? 'Click to stop simulation'
                          : 'Click to run simulation'
                        : 'No loads configured'
                    }
                  >
                    <div className="flex items-center gap-2 w-full">
                      {/* Button number */}
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium shrink-0',
                          isActive
                            ? 'bg-primary-foreground/20 text-primary-foreground'
                            : 'bg-primary/10 text-primary',
                        )}
                      >
                        {index + 1}
                      </div>

                      {/* Button label */}
                      <span
                        className={cn(
                          'text-sm font-medium truncate flex-1 text-left',
                          !hasCircuits && 'text-muted-foreground',
                        )}
                      >
                        {label}
                      </span>

                      {/* Load count indicator */}
                      {hasCircuits && (
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className={cn(
                                  'text-xs shrink-0 cursor-help',
                                  isActive ? 'text-primary-foreground/70' : 'text-muted-foreground',
                                )}
                              >
                                {circuitIds.length}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {circuitIds.length} load{circuitIds.length !== 1 ? 's' : ''}{' '}
                                assigned
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="px-4 pb-4">
          <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3">
            <p className="font-medium mb-1.5">Instructions:</p>
            <ul className="space-y-1">
              <li>• Click a button to toggle its simulation on/off</li>
              <li>• When active, all assigned loads will glow on the floor plan</li>
              <li>• Press ESC or click outside to clear all active simulations</li>
              <li>• Buttons without configured loads are disabled</li>
            </ul>
          </div>
        </div>
      </>
    );
  };

  const content = testModeKeypad ? (
    renderKeypadTestMode()
  ) : (
    <>
      {/* Add title for accessibility */}
      {isMobile ? (
        <DrawerHeader className="relative">
          <DrawerTitle>Presentation Mode</DrawerTitle>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={() => handleOpenChange(false)}
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" />
          </Button>
        </DrawerHeader>
      ) : (
        <SheetNoOverlayHeader className="sr-only">
          <SheetNoOverlayTitle>Presentation Mode</SheetNoOverlayTitle>
        </SheetNoOverlayHeader>
      )}
      <div className="flex flex-col h-full">
        <Tabs
          value={activeTab}
          onValueChange={value => setActiveTab(value as 'loads' | 'controls' | 'lv-equipment')}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="m-4 mb-0 grid grid-cols-3">
            <TabsTrigger value="loads">Loads</TabsTrigger>
            <TabsTrigger value="controls">Controls</TabsTrigger>
            <TabsTrigger value="lv-equipment">AV</TabsTrigger>
          </TabsList>
          <div className="flex-1 overflow-y-auto">
            <TabsContent value="loads" className="p-4 space-y-4">
              <div className="border rounded-lg p-4 space-y-3">
                <div className="bg-muted/50 border-l-4 border-muted-foreground px-4 py-2.5">
                  <h4 className="text-sm font-semibold">Loads List</h4>
                </div>
                {dataLoading ? (
                  <p className="text-sm text-muted-foreground">Loading circuits...</p>
                ) : sortedCircuits.length === 0 ? (
                  <div className="p-2 bg-muted/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">
                      No circuits found in this floor plan.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {sortedCircuits.map(circuit => {
                      const selected = presentationCircuitId === circuit.id;
                      const roomNames = getCircuitRooms(circuit.id);
                      return (
                        <button
                          key={circuit.id}
                          className={cn(
                            'w-full text-left px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-sm',
                            selected && 'bg-primary/10 font-semibold',
                          )}
                          onClick={() => {
                            handleSelectCircuit(selected ? null : circuit.id);
                          }}
                          data-circuit-id={circuit.id}
                        >
                          <span>
                            {circuit.sequence_number !== undefined &&
                            circuit.sequence_number !== null
                              ? `${circuit.sequence_number} - ${circuit.name}`
                              : circuit.name}
                          </span>
                          <span className="text-muted-foreground ml-2">- {roomNames}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="controls" className="p-4 space-y-4">
              {/* View mode toggle */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant={controlsViewMode === 'by-type' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setControlsViewMode('by-type')}
                    className="flex-1"
                  >
                    <LayoutGrid className="h-4 w-4 mr-1" />
                    By Type
                  </Button>
                  <Button
                    variant={controlsViewMode === 'by-room' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setControlsViewMode('by-room')}
                    className="flex-1"
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    By Room
                  </Button>
                </div>

                {controlsViewMode === 'by-type' ? (
                  // BY TYPE MODE: Type → Items (with room names)
                  <div className="space-y-3">
                    {['dimmer', 'switch', 'keypad', 'auxiliary', 'centralized-keypad'].map(
                      group => {
                        const groupControls = controls.filter(c => c.type === group);
                        if (groupControls.length === 0) return null;

                        return (
                          <div key={group} className="space-y-2">
                            <div className="bg-muted/50 border-l-4 border-muted-foreground px-4 py-2.5">
                              <h4 className="text-sm font-semibold capitalize">
                                {group.replace('-', ' ')}
                              </h4>
                            </div>
                            <div className="space-y-1">
                              {groupControls.map(ctrl => {
                                const room = ctrl.room_id
                                  ? rooms.find(r => r.id === ctrl.room_id)
                                  : null;
                                const roomName = room?.name || 'No room';
                                const selected = presentationControlId === ctrl.id;
                                const canTest =
                                  (ctrl.type === 'keypad' ||
                                    ctrl.type === 'centralized-keypad' ||
                                    ctrl.type === 'dimmer' ||
                                    ctrl.type === 'switch') &&
                                  hasConfiguredActions(ctrl);

                                return (
                                  <div
                                    key={ctrl.id}
                                    className={cn(
                                      'flex items-center gap-1 rounded-md transition-colors',
                                      selected && 'bg-primary/10',
                                    )}
                                  >
                                    <button
                                      className={cn(
                                        'flex-1 text-left px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-sm',
                                        selected && 'font-semibold',
                                      )}
                                      onClick={() => {
                                        handleSelectControl(selected ? null : ctrl.id);
                                      }}
                                      data-control-id={ctrl.id}
                                    >
                                      <span>{ctrl.name || ctrl.id.substring(0, 6)}</span>
                                      <span className="text-muted-foreground ml-2">
                                        - {roomName}
                                      </span>
                                    </button>
                                    {canTest && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 shrink-0"
                                        onClick={e => {
                                          e.stopPropagation();
                                          enterTestMode(ctrl);
                                        }}
                                        title="Test Mode"
                                      >
                                        <Keyboard className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                ) : (
                  // BY ROOM MODE: Room → Type → Items
                  (() => {
                    // Separate controls with and without rooms
                    const controlsWithRooms = controls.filter(c => c.room_id);
                    const controlsWithoutRooms = controls.filter(c => !c.room_id);

                    // Group controls by room, then by type
                    const roomMap = new Map<string, Map<string, typeof controls>>();

                    controlsWithRooms.forEach(control => {
                      const roomId = control.room_id!;
                      if (!roomMap.has(roomId)) {
                        roomMap.set(roomId, new Map());
                      }
                      const typeMap = roomMap.get(roomId)!;
                      const type = control.type;
                      if (!typeMap.has(type)) {
                        typeMap.set(type, []);
                      }
                      typeMap.get(type)!.push(control);
                    });

                    // Get sorted room IDs
                    const sortedRoomIds = Array.from(roomMap.keys()).sort((a, b) => {
                      const roomA = rooms.find(r => r.id === a)?.name || '';
                      const roomB = rooms.find(r => r.id === b)?.name || '';
                      return roomA.localeCompare(roomB);
                    });

                    return (
                      <div className="space-y-3">
                        {/* Rooms with controls */}
                        {sortedRoomIds.map(roomId => {
                          const room = rooms.find(r => r.id === roomId);
                          const roomName = room?.name || 'Unknown Room';
                          const typeMap = roomMap.get(roomId)!;
                          const sortedTypes = Array.from(typeMap.keys()).sort();

                          return (
                            <div key={roomId} className="space-y-2">
                              <div className="bg-muted/50 border-l-4 border-muted-foreground px-4 py-2.5">
                                <h4 className="text-sm font-semibold">{roomName}</h4>
                              </div>
                              {sortedTypes.map(type => {
                                const typeControls = typeMap.get(type)!;

                                return (
                                  <div key={`${roomId}-${type}`} className="ml-3 mb-3">
                                    <h4 className="text-sm font-medium mb-1 text-muted-foreground capitalize">
                                      {type.replace('-', ' ')}
                                    </h4>
                                    <div className="space-y-1 ml-2">
                                      {typeControls.map(ctrl => {
                                        const selected = presentationControlId === ctrl.id;
                                        const canTest =
                                          (ctrl.type === 'keypad' ||
                                            ctrl.type === 'centralized-keypad' ||
                                            ctrl.type === 'dimmer' ||
                                            ctrl.type === 'switch') &&
                                          hasConfiguredActions(ctrl);

                                        return (
                                          <div
                                            key={ctrl.id}
                                            className={cn(
                                              'flex items-center gap-1 rounded-md transition-colors',
                                              selected && 'bg-primary/10',
                                            )}
                                          >
                                            <button
                                              className={cn(
                                                'flex-1 text-left px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-sm',
                                                selected && 'font-semibold',
                                              )}
                                              onClick={() => {
                                                handleSelectControl(selected ? null : ctrl.id);
                                              }}
                                              data-control-id={ctrl.id}
                                            >
                                              {ctrl.name || ctrl.id.substring(0, 6)}
                                            </button>
                                            {canTest && (
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 shrink-0"
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  enterTestMode(ctrl);
                                                }}
                                                title="Test Mode"
                                              >
                                                <Keyboard className="h-4 w-4" />
                                              </Button>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}

                        {/* Controls without rooms (at the end) */}
                        {controlsWithoutRooms.length > 0 && (
                          <div className="space-y-2">
                            <div className="bg-muted/50 border-l-4 border-muted-foreground px-4 py-2.5">
                              <h4 className="text-sm font-semibold text-muted-foreground">
                                (No Room)
                              </h4>
                            </div>
                            {(() => {
                              // Group by type
                              const typeMap = new Map<string, typeof controls>();
                              controlsWithoutRooms.forEach(control => {
                                const type = control.type;
                                if (!typeMap.has(type)) {
                                  typeMap.set(type, []);
                                }
                                typeMap.get(type)!.push(control);
                              });

                              const sortedTypes = Array.from(typeMap.keys()).sort();

                              return sortedTypes.map(type => {
                                const typeControls = typeMap.get(type)!;

                                return (
                                  <div key={`no-room-${type}`} className="ml-3 mb-3">
                                    <h4 className="text-sm font-medium mb-1 text-muted-foreground capitalize">
                                      {type.replace('-', ' ')}
                                    </h4>
                                    <div className="space-y-1 ml-2">
                                      {typeControls.map(ctrl => {
                                        const selected = presentationControlId === ctrl.id;
                                        const canTest =
                                          (ctrl.type === 'keypad' ||
                                            ctrl.type === 'centralized-keypad' ||
                                            ctrl.type === 'dimmer' ||
                                            ctrl.type === 'switch') &&
                                          hasConfiguredActions(ctrl);

                                        return (
                                          <div
                                            key={ctrl.id}
                                            className={cn(
                                              'flex items-center gap-1 rounded-md transition-colors',
                                              selected && 'bg-primary/10',
                                            )}
                                          >
                                            <button
                                              className={cn(
                                                'flex-1 text-left px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-sm',
                                                selected && 'font-semibold',
                                              )}
                                              onClick={() => {
                                                handleSelectControl(selected ? null : ctrl.id);
                                              }}
                                              data-control-id={ctrl.id}
                                            >
                                              {ctrl.name || ctrl.id.substring(0, 6)}
                                            </button>
                                            {canTest && (
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 shrink-0"
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  enterTestMode(ctrl);
                                                }}
                                                title="Test Mode"
                                              >
                                                <Keyboard className="h-4 w-4" />
                                              </Button>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            </TabsContent>
            <TabsContent value="lv-equipment" className="p-4 space-y-4">
              {/* View mode toggle */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant={lvViewMode === 'by-category' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setLvViewMode('by-category')}
                    className="flex-1"
                  >
                    <LayoutGrid className="h-4 w-4 mr-1" />
                    By Category
                  </Button>
                  <Button
                    variant={lvViewMode === 'by-room' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setLvViewMode('by-room')}
                    className="flex-1"
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    By Room
                  </Button>
                </div>

                {lvViewMode === 'by-category' ? (
                  // BY CATEGORY MODE: Category → Items (with room names)
                  <div className="space-y-3">
                    {(() => {
                      // Get unique categories and separate products with/without rooms
                      const productsWithRooms = lvProducts.filter(p => p.room_id);
                      const productsWithoutRooms = lvProducts.filter(p => !p.room_id);

                      // Group products by category
                      const categoryMap = new Map<string, typeof lvProducts>();

                      [...productsWithRooms, ...productsWithoutRooms].forEach(product => {
                        const category = product.category || 'Uncategorized';
                        if (!categoryMap.has(category)) {
                          categoryMap.set(category, []);
                        }
                        categoryMap.get(category)!.push(product);
                      });

                      // Sort categories alphabetically
                      const sortedCategories = Array.from(categoryMap.keys()).sort();

                      return sortedCategories.map(category => {
                        const categoryProducts = categoryMap.get(category)!;
                        const formattedCategory = formatCategoryName(category);

                        // Sort products within category by room first, then by subcategory/name
                        const sortedProducts = [...categoryProducts].sort((a, b) => {
                          const roomA = a.room_id ? rooms.find(r => r.id === a.room_id) : null;
                          const roomB = b.room_id ? rooms.find(r => r.id === b.room_id) : null;
                          const roomNameA = roomA?.name || 'zzz_no_room'; // Sort no-room items last
                          const roomNameB = roomB?.name || 'zzz_no_room';

                          // First sort by room name
                          const roomCompare = roomNameA.localeCompare(roomNameB);
                          if (roomCompare !== 0) return roomCompare;

                          // If same room, sort by subcategory then name
                          const subcatA = a.subcategory || '';
                          const subcatB = b.subcategory || '';
                          const subcatCompare = subcatA.localeCompare(subcatB);
                          if (subcatCompare !== 0) return subcatCompare;

                          // Finally by product name
                          return a.name.localeCompare(b.name);
                        });

                        return (
                          <div key={category} className="space-y-2">
                            <div className="bg-muted/50 border-l-4 border-muted-foreground px-4 py-2.5">
                              <h4 className="text-sm font-semibold">{formattedCategory}</h4>
                            </div>
                            <div className="space-y-1">
                              {sortedProducts.map(product => {
                                const room = product.room_id
                                  ? rooms.find(r => r.id === product.room_id)
                                  : null;
                                const roomName = room?.name || 'No room';
                                const selected = presentationLVProductIds.includes(product.id);
                                const productGroupKey = getProductGroupingKey(product);

                                return (
                                  <button
                                    key={product.id}
                                    className={cn(
                                      'w-full text-left px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-sm',
                                      selected && 'bg-primary/10 font-semibold',
                                    )}
                                    onClick={() => {
                                      handleSelectLVProduct(
                                        selected ? null : product.id,
                                        productGroupKey,
                                      );
                                    }}
                                    data-lv-product-id={product.id}
                                  >
                                    <span>{product.name}</span>
                                    <span className="text-muted-foreground ml-2">- {roomName}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  // BY ROOM MODE: Room → Category → Items
                  (() => {
                    // Separate products with and without rooms
                    const productsWithRooms = lvProducts.filter(p => p.room_id);
                    const productsWithoutRooms = lvProducts.filter(p => !p.room_id);

                    // Group products by room, then by category, then by subcategory
                    const roomMap = new Map<
                      string,
                      Map<string, Map<string | null, typeof lvProducts>>
                    >();

                    productsWithRooms.forEach(product => {
                      const roomId = product.room_id!;
                      const category = product.category || 'Uncategorized';
                      const subcategory = product.subcategory || null;

                      if (!roomMap.has(roomId)) {
                        roomMap.set(roomId, new Map());
                      }
                      const categoryMap = roomMap.get(roomId)!;
                      if (!categoryMap.has(category)) {
                        categoryMap.set(category, new Map());
                      }
                      const subcategoryMap = categoryMap.get(category)!;
                      if (!subcategoryMap.has(subcategory)) {
                        subcategoryMap.set(subcategory, []);
                      }
                      subcategoryMap.get(subcategory)!.push(product);
                    });

                    // Get sorted room IDs
                    const sortedRoomIds = Array.from(roomMap.keys()).sort((a, b) => {
                      const roomA = rooms.find(r => r.id === a)?.name || '';
                      const roomB = rooms.find(r => r.id === b)?.name || '';
                      return roomA.localeCompare(roomB);
                    });

                    return (
                      <div className="space-y-3">
                        {/* Rooms with products */}
                        {sortedRoomIds.map(roomId => {
                          const room = rooms.find(r => r.id === roomId);
                          const roomName = room?.name || 'Unknown Room';
                          const categoryMap = roomMap.get(roomId)!;
                          const sortedCategories = Array.from(categoryMap.keys()).sort();

                          return (
                            <div key={roomId} className="space-y-2">
                              <div className="bg-muted/50 border-l-4 border-muted-foreground px-4 py-2.5">
                                <h4 className="text-sm font-semibold">{roomName}</h4>
                              </div>
                              {sortedCategories.map(category => {
                                const subcategoryMap = categoryMap.get(category)!;
                                const sortedSubcategories = Array.from(subcategoryMap.keys()).sort(
                                  (a, b) => {
                                    // null (no subcategory) should come last
                                    if (a === null) return 1;
                                    if (b === null) return -1;
                                    return a.localeCompare(b);
                                  },
                                );
                                const formattedCategory = formatCategoryName(category);

                                return (
                                  <div key={`${roomId}-${category}`} className="ml-3 mb-3">
                                    <h4 className="text-sm font-medium mb-1 text-muted-foreground">
                                      {formattedCategory}
                                    </h4>
                                    <div className="space-y-1 ml-2">
                                      {sortedSubcategories.map(subcategory => {
                                        const subcategoryProducts =
                                          subcategoryMap.get(subcategory)!;
                                        const representativeProduct = subcategoryProducts[0];
                                        const selected = presentationLVProductIds.includes(
                                          representativeProduct.id,
                                        );

                                        // Display name: subcategory if exists, otherwise product name
                                        const displayName = subcategory
                                          ? formatCategoryName(subcategory)
                                          : representativeProduct.name;

                                        // Grouping key for blinking: use subcategory if exists, otherwise category
                                        const groupKey =
                                          getProductGroupingKey(representativeProduct);

                                        return (
                                          <button
                                            key={`${roomId}-${category}-${subcategory || 'no-sub'}`}
                                            className={cn(
                                              'w-full text-left px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-sm',
                                              selected && 'bg-primary/10 font-semibold',
                                            )}
                                            onClick={() => {
                                              handleSelectLVProduct(
                                                selected ? null : representativeProduct.id,
                                                groupKey,
                                              );
                                            }}
                                            data-lv-product-id={representativeProduct.id}
                                          >
                                            {displayName}
                                            {subcategoryProducts.length > 1 && (
                                              <span className="text-muted-foreground ml-2">
                                                ({subcategoryProducts.length})
                                              </span>
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}

                        {/* Products without rooms (at the end) */}
                        {productsWithoutRooms.length > 0 && (
                          <div className="space-y-2">
                            <div className="bg-muted/50 border-l-4 border-muted-foreground px-4 py-2.5">
                              <h4 className="text-sm font-semibold text-muted-foreground">
                                (No Room)
                              </h4>
                            </div>
                            {(() => {
                              // Group by category, then by subcategory
                              const categoryMap = new Map<
                                string,
                                Map<string | null, typeof lvProducts>
                              >();
                              productsWithoutRooms.forEach(product => {
                                const category = product.category || 'Uncategorized';
                                const subcategory = product.subcategory || null;

                                if (!categoryMap.has(category)) {
                                  categoryMap.set(category, new Map());
                                }
                                const subcategoryMap = categoryMap.get(category)!;
                                if (!subcategoryMap.has(subcategory)) {
                                  subcategoryMap.set(subcategory, []);
                                }
                                subcategoryMap.get(subcategory)!.push(product);
                              });

                              const sortedCategories = Array.from(categoryMap.keys()).sort();

                              return sortedCategories.map(category => {
                                const subcategoryMap = categoryMap.get(category)!;
                                const sortedSubcategories = Array.from(subcategoryMap.keys()).sort(
                                  (a, b) => {
                                    if (a === null) return 1;
                                    if (b === null) return -1;
                                    return a.localeCompare(b);
                                  },
                                );
                                const formattedCategory = formatCategoryName(category);

                                return (
                                  <div key={`no-room-${category}`} className="ml-3 mb-3">
                                    <h4 className="text-sm font-medium mb-1 text-muted-foreground">
                                      {formattedCategory}
                                    </h4>
                                    <div className="space-y-1 ml-2">
                                      {sortedSubcategories.map(subcategory => {
                                        const subcategoryProducts =
                                          subcategoryMap.get(subcategory)!;
                                        const representativeProduct = subcategoryProducts[0];
                                        const selected = presentationLVProductIds.includes(
                                          representativeProduct.id,
                                        );

                                        const displayName = subcategory
                                          ? formatCategoryName(subcategory)
                                          : representativeProduct.name;

                                        const groupKey =
                                          getProductGroupingKey(representativeProduct);

                                        return (
                                          <button
                                            key={`no-room-${category}-${subcategory || 'no-sub'}`}
                                            className={cn(
                                              'w-full text-left px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-sm',
                                              selected && 'bg-primary/10 font-semibold',
                                            )}
                                            onClick={() => {
                                              handleSelectLVProduct(
                                                selected ? null : representativeProduct.id,
                                                groupKey,
                                              );
                                            }}
                                            data-lv-product-id={representativeProduct.id}
                                          >
                                            {displayName}
                                            {subcategoryProducts.length > 1 && (
                                              <span className="text-muted-foreground ml-2">
                                                ({subcategoryProducts.length})
                                              </span>
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </>
  );

  return (
    <Panel open={open} onOpenChange={handleOpenChange} modal={false}>
      <PanelContent
        data-presentation-drawer
        className="w-[320px] sm:w-[380px] bg-background/95 backdrop-blur-sm border-l"
      >
        {content}
      </PanelContent>
    </Panel>
  );
}
